import React from 'react';
import { Box, Tab, Tabs as MuiTabs, Badge, Typography } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { TestDataAttribute } from '../testDataAttribute';
import { PORTAL_COLORS, sanitizeTestId, toTitleCaseIfAllCaps } from '@roc/feature-utils';
import { opacity } from 'html2canvas/dist/types/css/property-descriptors/opacity';

const StyledBadge = withStyles(theme => ({
  badge: {
    right: -10,
    top: 6,
    border: `2px solid #fff`,
    padding: '4px',
    fontSize: '13px',
    backgroundColor: PORTAL_COLORS.pendingDocuments,
    color: 'white',
    fontWeight: 700,
    userSelect: 'none',
    height: 'auto',
    opacity: '1 !important',
  },
}))(Badge);

export type Tab = {
  label: string;
  right?: React.ReactNode;
  showBadge?: boolean;
  disabled?: boolean;
};

export type TabsProps = {
  className?: string;
  tabs: Tab[];
  selectedTab: number;
  onTabChange: (e: any, index: number) => void;
  right?: React.ReactNode;
  variant?: any;
  scrollButtons?: any;
  tabClassName?: string;
  tabSelectedClassName?: string;
  /** drop the vertical padding around the tab strip (e.g. sub tabs directly under another tab bar) */
  disablePadding?: boolean;
};

// Pill tab styling comes from the MUI theme (MuiTabs / MuiTab overrides)
const useToolbarStyles = makeStyles(({ spacing, palette }: Theme) => {
  return {
    // No card behind the tab strip: tabs sit on the page background
    root: {
      width: '100%',
      background: 'transparent',
    },
  };
});

const useTabsStyles = makeStyles(({ palette }: Theme) => {
  return {
    disabled: {
      color: palette.text.disabled,
    },
  };
});

const useTabItemStyles = makeStyles(({ spacing, breakpoints }: Theme) => {
  const tabsGutter = spacing(2);
  return {
    root: {
      padding: `6px ${tabsGutter * 1.5}px`,
      minWidth: 0,
      [breakpoints.up('md')]: {
        minWidth: 0,
      },
      '&.MuiButtonBase-root.MuiTab-root': {
        opacity: '1 !important',
      },
    },
    selected: {
      zIndex: 1,
    },
    wrapper: {
      whiteSpace: 'nowrap',
    },
  };
});

const useTabItemWithBadgeStyles = makeStyles(({ spacing, breakpoints }: Theme) => {
  const tabsGutter = spacing(2);
  return {
    root: {
      padding: `6px ${tabsGutter * 1.5}px`,
      minWidth: '165px !important',
      [breakpoints.up('md')]: {
        minWidth: 0,
      },
      '&.MuiButtonBase-root.MuiTab-root': {
        opacity: '1 !important',
      },
    },
    selected: {
      zIndex: 1,
    },
    wrapper: {
      whiteSpace: 'nowrap',
    },
  };
});

export const Tabs = ({
  className,
  tabs,
  selectedTab,
  onTabChange,
  right,
  variant,
  scrollButtons,
  tabClassName,
  tabSelectedClassName,
  disablePadding = false,
}: TabsProps) => {
  const toolbarStyles = useToolbarStyles();
  const tabsStyles = useTabsStyles();
  const tabItemStyles = useTabItemStyles();
  const tabItemWithBadgeStyles = useTabItemWithBadgeStyles();
  const testId = sanitizeTestId;

  return (
    <Box
      display="flex"
      alignItems="center"
      className={clsx([toolbarStyles.root, className])}
    >
      <Box flexGrow={1} minWidth={0} py={disablePadding ? 0 : 1.5}>
        <MuiTabs
          value={selectedTab}
          onChange={onTabChange}
          // scrollable by default: when the tabs don't fit, the strip scrolls instead of widening the page
          variant={variant ?? 'scrollable'}
          scrollButtons={scrollButtons ?? 'off'}
        >
          {tabs.map(tab => (
            <Tab
              key={tab.label}
              classes={tab.showBadge ? tabItemWithBadgeStyles : tabItemStyles}
              // tabClassName / tabSelectedClassName are intentionally not applied: tabs use the
              // platform pill style from the MUI theme (MuiTabs / MuiTab overrides) everywhere.
              disableRipple
              disabled={tab.disabled}
              label={
                <TestDataAttribute id={testId(tab.label)}>
                  <Box className={clsx(tab.disabled && tabsStyles.disabled)}>
                    {tab.showBadge ?
                      <StyledBadge badgeContent={tab.showBadge}>
                        {toTitleCaseIfAllCaps(tab.label)}
                        {tab.right}
                      </StyledBadge>
                      :
                      <>
                        {toTitleCaseIfAllCaps(tab.label)}
                        {tab.right}
                      </>
                    }
                  </Box>
                </TestDataAttribute>
              }
            />
          ))}
        </MuiTabs>
      </Box>
      {right}
    </Box>
  );
};
